import {
  InputAdornment,
  InputBaseProps,
  outlinedInputClasses,
  TextField as MuiTextField,
  OutlinedInputProps,
  OutlinedTextFieldProps,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { faXmarkCircleRegular } from "@outschool/icons";
import React from "react";

import { IconButton } from "../IconButton/IconButton";
import { OsField, OsFieldProps } from "../OsField/OsField";

export const OsTextFieldSizes = ["xsmall", "small", "medium"] as const;
export type OsTextFieldSize = (typeof OsTextFieldSizes)[number];

const SIZE_CONFIG = {
  xsmall: { minHeight: 40, padding: 8 },
  small: { minHeight: 48, padding: 12 },
  medium: { minHeight: 56, padding: 16 },
} as const;

type OsTextFieldBaseProps = Omit<
  OutlinedTextFieldProps,
  "variant" | "size" | "InputProps" | "label" | "error" | "sx"
> &
  Pick<OsFieldProps, "description" | "tooltipText" | "errorMessage"> & {
    /**
     * Props applied to the Input element.
     * It will be a [`FilledInput`](/material-ui/api/filled-input/),
     * [`OutlinedInput`](/material-ui/api/outlined-input/) or [`Input`](/material-ui/api/input/)
     * component depending on the `variant` prop value.
     */
    InputProps?: Partial<InputBaseProps & OutlinedInputProps>;
    /** Size of the text field. Defaults to "small". */
    size?: OsTextFieldSize;
    /** When true, shows a clear button when the input has content. Defaults to false. */
    showClearButton?: boolean;
  };

/**
 * At least one of `label` or `ariaLabel` must be provided for accessibility.
 * When a visible `label` is present, `ariaLabel` is optional.
 */
export type OsTextFieldProps = OsTextFieldBaseProps &
  (
    | { label: React.ReactNode; ariaLabel?: string }
    | { label?: never; ariaLabel: string }
  );

export const OsTextField = ({
  ariaLabel,
  description,
  tooltipText,
  label,
  required,
  errorMessage,
  disabled,
  helperText,
  fullWidth,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  InputProps,
  inputRef: externalInputRef,
  inputProps,
  select,
  size = "small",
  showClearButton = false,
  ...props
}: OsTextFieldProps) => {
  const hasError = !!errorMessage;
  const internalInputRef = React.useRef<HTMLInputElement | null>(null);
  const [hasContent, setHasContent] = React.useState(
    () => !!(value ?? defaultValue),
  );
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    if (value !== undefined) {
      setHasContent(!!value);
    }
  }, [value]);

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    setHasContent(!!e.target.value);
    onChange?.(e);
  };

  const handleFocus: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleClear = () => {
    const input = internalInputRef.current;
    if (!input) {
      return;
    }

    const proto = Object.getPrototypeOf(input);
    const nativeValueSetter = Object.getOwnPropertyDescriptor(
      proto,
      "value",
    )?.set;
    nativeValueSetter?.call(input, "");
    input.dispatchEvent(new Event("input", { bubbles: true }));
    setHasContent(false);
  };

  const mergedInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      internalInputRef.current = node;
      if (typeof externalInputRef === "function") {
        externalInputRef(node);
      } else if (externalInputRef && typeof externalInputRef === "object") {
        (externalInputRef as { current: HTMLInputElement | null }).current =
          node;
      }
    },
    [externalInputRef],
  );

  const isClearVisible = showClearButton && hasContent && isFocused && !select;

  const clearAdornment = showClearButton ? (
    <InputAdornment
      position="end"
      sx={{ visibility: isClearVisible ? "visible" : "hidden" }}
    >
      <IconButton
        aria-label="Clear"
        tabIndex={isClearVisible ? 0 : -1}
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        onClick={handleClear}
        // eslint-disable-next-line i18next/no-literal-string
        edge="end"
        size="small"
        variant="text"
        color="neutral"
        icon={faXmarkCircleRegular}
        sx={{ fontSize: 18 }}
      />
    </InputAdornment>
  ) : undefined;

  const mergedInputProps: OsTextFieldProps["InputProps"] = {
    ...InputProps,
    ...(clearAdornment && {
      endAdornment: (
        <>
          {clearAdornment}
          {InputProps?.endAdornment}
        </>
      ),
    }),
  };

  const { minHeight, padding } = SIZE_CONFIG[size];

  const inputSx: SxProps<Theme> = {
    [`& .${outlinedInputClasses.root}`]: {
      minHeight: t => t.spacing(minHeight),
      "&.MuiInputBase-multiline": {
        padding: t => t.spacing(padding),
      },
      [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: "grey.700",
      },
      [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: "primary.main",
        borderWidth: 1,
      },
      [`&.Mui-error .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: "error.main",
        borderWidth: 1,
      },
      [`&.Mui-disabled`]: {
        backgroundColor: "grey.100",
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: "grey.200",
        },
      },
    },
    [`& .${outlinedInputClasses.input}`]: {
      typography: "body1",
      fontWeight: "fontWeightRegular",
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
      padding: t => t.spacing(padding),
      "&.MuiInputBase-inputMultiline": {
        padding: 0,
      },
      "&::placeholder": {
        color: "grey.600",
        opacity: 1,
      },
      "&.Mui-disabled": {
        color: "grey.500",
        WebkitTextFillColor: "unset",
      },
    },
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: "grey.300",
      top: 0,
      "& legend": { display: "none" },
    },
  };

  return (
    <OsField
      label={label}
      description={description}
      tooltipText={tooltipText}
      helperText={helperText}
      errorMessage={errorMessage}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      <MuiTextField
        {...props}
        select={select}
        error={hasError}
        disabled={disabled}
        fullWidth={fullWidth}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        InputProps={mergedInputProps}
        inputProps={{ ...inputProps, "aria-label": ariaLabel }}
        inputRef={mergedInputRef}
        sx={inputSx}
      />
    </OsField>
  );
};
